/**
 * Wire contracts for the v2 Data Studio asset APIs, transcribed verbatim from
 * the backend OpenAPI spec (`doc-hub` @ `feat/add-data-studio-apis`).
 *
 * These are the **wire layer**: snake_case keys, nullability exactly as the
 * spec declares it, no derived or convenience fields. The UI never reads them
 * directly — `dataStudioAssetMappers.ts` maps them onto the existing
 * `dataStudioTables.types.ts` / `dataStudioColumnDetails.types.ts` contracts.
 * Keeping the two layers apart is what confines a spec change to the mapper.
 *
 * Identity is `(connection_id, asset_id[, column_id])` — there is no
 * `workspace_id` on any v2 path.
 *
 * Endpoint letters below match `DATA_STUDIO_API_ALIGNMENT_PLAN.md` §2.
 */
import type {
  ConnectorAssetType,
  ConnectorType,
} from "../connectors/connectorAsset.types";

export type { ConnectorAssetType, ConnectorType };

// ---------------------------------------------------------------------------
// Shared envelopes
// ---------------------------------------------------------------------------

/** Generic limit/offset pagination metadata, reused across v2 asset sub-resources. */
export interface OffsetPaginationMeta {
  limit: number;
  offset: number;
  /** Total items available, independent of `limit`/`offset`. */
  total_count: number;
  /** True if there are more items to fetch beyond this page. */
  has_next: boolean;
}

/**
 * `connector_connections.id` plus the catalog key — the nested identity every
 * v2 asset response carries. `id` is the same value used as the
 * `connection_id` path parameter and query filter.
 */
export interface ConnectorAssetConnectorRef {
  id: string;
  type: ConnectorType;
}

/**
 * Machine-readable error body returned by every 4xx/5xx from the backend's
 * global exception handlers. `getApiErrorMessage` already reads
 * `error.message` from this shape — see services/apiErrors.ts.
 */
export interface DomainErrorDetail {
  /** e.g. `NOT_FOUND`, `CONFLICT`, `VALIDATION_ERROR`, `FORBIDDEN`. */
  code: string;
  message: string;
  /** Also present in server logs for this request. */
  correlation_id: string;
  /**
   * Populated only for 422s raised by request-body/query validation — the raw
   * per-field error list. Absent for domain errors (404/409/...).
   */
  details?: unknown;
}

export interface DomainErrorResponse {
  success: false;
  error: DomainErrorDetail;
}

// ---------------------------------------------------------------------------
// A. GET /api/v2/assets — Search Assets Globally
// ---------------------------------------------------------------------------

/** `sort_by` accepts `name` only today. */
export type AssetSearchSortBy = "name";
export type AssetSearchSortOrder = "asc" | "desc";

/**
 * Query params for endpoint A, snake_case as sent on the wire. Array params
 * serialize comma-separated (`style: form`, `explode: false`).
 *
 * Filters compose with AND across params, OR within one list. `search` is a
 * case-insensitive substring match against `name` **only** — not
 * `qualified_name`, not `display_name`.
 */
export interface SearchAssetsQuery {
  asset_type?: ConnectorAssetType[];
  connection_id?: string[];
  search?: string | null;
  /** 1–200, backend default 50. */
  limit?: number;
  /** >= 0, backend default 0. */
  offset?: number;
  sort_by?: AssetSearchSortBy;
  sort_order?: AssetSearchSortOrder;
}

/**
 * One hit in endpoint A — a lightweight cross-connector asset summary. Every
 * field is required; `parent_asset_id` is nullable but always present.
 */
export interface AssetSearchResult {
  id: string;
  tenant_id: string;
  connector: ConnectorAssetConnectorRef;
  parent_asset_id: string | null;
  asset_type: ConnectorAssetType;
  name: string;
  qualified_name: string;
  is_selected: boolean;
  is_enabled: boolean;
  discovered_at: string;
  /** A Sheets tab's spreadsheet name; null/absent for other assets. */
  parent_name?: string | null;
}

export interface AssetSearchListResponse {
  meta: OffsetPaginationMeta;
  data: AssetSearchResult[];
}

// ---------------------------------------------------------------------------
// B. GET /api/v2/connectors/{connection_id}/assets/{asset_id} — Get Asset
// ---------------------------------------------------------------------------

/**
 * Type-specific attributes for tabular assets. The spec marks **every** field
 * optional, and `TabularAsset.attributes` itself is optional, so a conformant
 * response may omit all of these — the header must render without them.
 */
export interface TabularAssetAttributes {
  row_count?: number;
  column_count?: number;
  /**
   * Overall score for the asset, 0-100 — an aggregate of the asset's columns'
   * own `ConnectorColumn.data_quality_score` values. Fractional here, unlike
   * the integer per-column score.
   */
  data_quality_score?: number;
}

/** `asset_type` values that resolve to `TabularAsset` in the `AssetV2` union. */
export type TabularAssetType = "relational_table" | "sheet" | "relational_view";

/** Asset shape for a table-like asset — the only member of `AssetV2` today. */
export interface TabularAsset {
  id: string;
  tenant_id: string;
  connector: ConnectorAssetConnectorRef;
  asset_type: TabularAssetType;
  name: string;
  attributes?: TabularAssetAttributes;
}

/**
 * Discriminated union returned by endpoint B, discriminator `asset_type`.
 * Only the three tabular types are defined today — attribute schemas for
 * other asset types (e.g. `bi_chart`) join this union as they are specified,
 * so widen this alias rather than branching at call sites.
 */
export type AssetV2 = TabularAsset;

// ---------------------------------------------------------------------------
// C / D. Columns
// ---------------------------------------------------------------------------

/**
 * Free-form profiling metrics keyed by the backend, e.g. `null_count`,
 * `distinct_count`, `min`/`min_value`, `max`/`max_value`. The spec declares
 * `additionalProperties: true` with no fixed key set, so values are raw (not
 * display-formatted) and the frontend owns labelling and ordering.
 *
 * See `DATA_STUDIO_API_ALIGNMENT_PLAN.md` §7 Q2 — the exact key set per column
 * kind is still outstanding with the backend.
 */
export type ColumnProfilingSummary = Record<string, unknown>;

/** A complete Vega-Lite spec for the column's profiling preview. */
export type ColumnProfilingPreviewSpec = Record<string, unknown>;

/** Optional fields appended to endpoint C's columns via `include`. */
export type ColumnInclude = "profiling_summary" | "profiling_preview_spec";

export const COLUMN_INCLUDE_ALL: readonly ColumnInclude[] = [
  "profiling_summary",
  "profiling_preview_spec",
] as const;

/** Query params for endpoint C. */
export interface ListAssetColumnsQuery {
  /** 1–200, backend default 50. */
  limit?: number;
  /** >= 0, backend default 0. */
  offset?: number;
  /**
   * Comma-separated optional fields. Omitted by default because both are
   * heavy — a column arrives without them unless asked for.
   */
  include?: ColumnInclude[];
}

/**
 * A column (`relational_column` / `sheet_column`) of a tabular asset.
 *
 * Named `ConnectorColumn` rather than the spec's bare `Column` to avoid
 * colliding with the grid's own column types at import sites.
 */
export interface ConnectorColumn {
  /** The column's own `connector_assets.id`. */
  id: string;
  name: string;
  /** Underlying source data type, e.g. `integer`, `varchar`. */
  data_type: string;
  is_nullable: boolean;
  is_primary_key: boolean;
  is_foreign_key: boolean;
  /** **1-based** left-to-right position of the column in the table. */
  ordinal_position: number;
  /**
   * This column's own score, 0-100 — one of the inputs rolled up into the
   * parent asset's `TabularAssetAttributes.data_quality_score`. Always
   * present; `null` if this column has not been scored yet.
   */
  data_quality_score: number | null;
  /** Present only when requested via `include=profiling_summary`. */
  profiling_summary?: ColumnProfilingSummary | null;
  /** Present only when requested via `include=profiling_preview_spec`. */
  profiling_preview_spec?: ColumnProfilingPreviewSpec | null;
}

export interface ColumnListResponse {
  meta: OffsetPaginationMeta;
  data: ConnectorColumn[];
}

/**
 * Endpoint D — every `ConnectorColumn` field plus the curated ones, with
 * profiling **always** present (nullable when the column has not been
 * profiled), so no `include` parameter is needed.
 */
export interface ColumnDetail extends ConnectorColumn {
  /** Human-readable description of the column's business purpose. */
  description: string | null;
  /** Assorted business rules, warnings, or historical context. */
  notes: string[] | null;
  /** Alternative names this column is known by. */
  synonyms: string[] | null;
  profiling_summary: ColumnProfilingSummary | null;
  profiling_preview_spec: ColumnProfilingPreviewSpec | null;
}

/**
 * PATCH body for endpoint D'. Every field is optional but **at least one must
 * be present** (`minProperties: 1`) — an empty body is a `400`.
 *
 * Semantics, exactly as specified:
 * - an omitted field is left unchanged;
 * - a field set to `null` clears it;
 * - `notes` / `synonyms` replace the whole list — this is not an append.
 *
 * These three are the only writable fields on a column. There is no `version`
 * or ETag, so there is no optimistic concurrency: last write wins.
 */
export interface ColumnCurationUpdate {
  description?: string | null;
  notes?: string[] | null;
  synonyms?: string[] | null;
}

/** True when a curation body carries at least one field, i.e. avoids the `400`. */
export const hasColumnCurationChanges = (
  update: ColumnCurationUpdate,
): boolean =>
  (Object.keys(update) as Array<keyof ColumnCurationUpdate>).some(
    (field) => update[field] !== undefined,
  );

// ---------------------------------------------------------------------------
// E. GET .../assets/{asset_id}/rows — Fetch Data Rows
// ---------------------------------------------------------------------------

/**
 * Query params for endpoint E. Row and column pagination are independent.
 * Passing `column_ids` switches to explicit column selection and makes
 * `column_limit`/`column_offset` ignored server-side.
 */
export interface ListAssetRowsQuery {
  /** Rows per page, 1–1000, backend default 100. */
  limit?: number;
  /** Rows to skip, >= 0, backend default 0. */
  offset?: number;
  /** Columns per page, 1–200, backend default 50. Ignored with `column_ids`. */
  column_limit?: number;
  /** Columns to skip, >= 0, backend default 0. Ignored with `column_ids`. */
  column_offset?: number;
  /**
   * Exact column ids to fetch, **in the desired display order** — e.g. the
   * result of the user reordering, hiding, or pinning columns. Overrides
   * `column_limit`/`column_offset` entirely when provided.
   */
  column_ids?: string[];
}

/**
 * Lightweight column metadata returned inline with endpoint E so the grid can
 * render without first calling endpoint C. A subset of `ConnectorColumn`.
 */
export interface RowColumnSummary {
  id: string;
  name: string;
  data_type: string;
}

/** Independent pagination state for the row and column dimensions. */
export interface RowsMeta {
  rows: OffsetPaginationMeta;
  columns: OffsetPaginationMeta;
}

/** A single cell. A genuinely absent value is `null`, never omitted. */
export type RowCellValue = string | number | boolean | null;

/** One row: cell values ordered to match `RowsResponse.columns`. */
export type RowTuple = RowCellValue[];

export interface RowsResponse {
  meta: RowsMeta;
  /**
   * Columns for this page, positionally aligned with each row in `data`.
   * Reflects `column_ids` when given, otherwise
   * `column_limit`/`column_offset`.
   */
  columns: RowColumnSummary[];
  /**
   * Rows for this page, each positionally aligned with `columns` — so column
   * alignment never shifts.
   */
  data: RowTuple[];
}

// ---------------------------------------------------------------------------
// Path identity
// ---------------------------------------------------------------------------

/**
 * The `(connection_id, asset_id)` pair every per-asset v2 endpoint needs.
 * Endpoint A is cross-connector, so a selected asset carries its own
 * `connectionId` (from `AssetSearchResult.connector.id`) — the Data Studio
 * route param is a filter, not the identity.
 */
export interface ConnectorAssetIdentity {
  connectionId: string;
  assetId: string;
  /**
   * Set for a bridged source (sheet tab, SaaS object): the connector type's catalog key. Its
   * columns and rows then come from the live sample instead of the v2 endpoints.
   */
  liveSampleSlug?: string | null;
}

export interface ConnectorColumnIdentity extends ConnectorAssetIdentity {
  columnId: string;
}
